import React from "react";
import { Link } from "react-router-dom";
import { Award, ArrowUpRight } from "lucide-react";
import { AWARDS, AWARD_YEAR, DISTINCTIONS, avgScore, distinctionOf } from "../mock";
import { usePlaces } from "../context/PlacesContext";
import { SectionLabel } from "../components/Bits";

export default function Awards() {
  const { getPlace, loading } = usePlaces();

  if (loading) {
    return <div className="pt-[140px] pb-40 text-center text-ink-soft font-serif text-2xl">Loading…</div>;
  }

  const withWinners = AWARDS.map((a) => ({ a, w: getPlace(a.winnerSlug) })).filter((x) => x.w);
  if (!withWinners.length) {
    return <div className="pt-[140px] pb-40 text-center font-serif text-3xl">Awards coming soon.</div>;
  }
  const [{ a: hero, w: heroPlace }, ...rest] = withWinners;

  return (
    <div className="pt-[68px]">
      <div className="bg-ink text-paper relative grain overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 relative">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-brass" />
            <span className="eyebrow text-brass">The Placeworthy Awards {AWARD_YEAR}</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl font-semibold leading-[1.0] display-balance max-w-3xl">
            The best of the year, decided the honest way.
          </h1>
          <p className="text-paper/60 mt-6 text-lg max-w-xl leading-relaxed">
            One winner per category. No public voting, no paid entries — just the places our reviewers
            would return to first.
          </p>
        </div>
      </div>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <Link to={`/place/${heroPlace.slug}`} className="group grid lg:grid-cols-2 gap-10 items-center">
          <div className="img-zoom relative aspect-[4/3] bg-ink/5">
            <img src={heroPlace.image} alt={heroPlace.name} className="w-full h-full object-cover" />
            <div className="absolute top-5 left-5 flex items-center gap-2 bg-brass text-ink px-4 py-2">
              <Award className="w-4 h-4" />
              <span className="eyebrow text-[10px]">{hero.title}</span>
            </div>
          </div>
          <div>
            <span className="eyebrow text-claret text-[10px]">Highest honour · {AWARD_YEAR}</span>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl font-semibold mt-2 group-hover:text-claret transition-colors">{heroPlace.name}</h2>
            <p className="text-ink-soft text-sm mt-3">{heroPlace.neighborhood} · {heroPlace.city} · {heroPlace.cuisine}</p>
            <p className="text-ink-soft text-lg mt-5 leading-relaxed">{hero.note}</p>
            <span className="inline-flex items-center gap-2 mt-6 text-sm font-semibold link-underline">
              Read the review <ArrowUpRight className="w-4 h-4" />
            </span>
          </div>
        </Link>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
        <SectionLabel>The {AWARD_YEAR} winners</SectionLabel>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-10">
          {rest.map(({ a, w }, i) => (
            <Link key={a.id} to={`/place/${w.slug}`} className="group rise-in" style={{ animationDelay: `${i * 70}ms` }}>
              <div className="img-zoom relative aspect-[3/2] bg-ink/5">
                <img src={w.image} alt={w.name} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
                <div className="absolute top-4 left-4 flex items-center gap-1.5 text-paper">
                  <Award className="w-4 h-4 text-brass" />
                  <span className="eyebrow text-[10px] text-brass">{a.title}</span>
                </div>
                <span className="absolute bottom-4 right-4 flex items-center gap-1">
                  {Array.from({ length: distinctionOf(w) }).map((_, i) => (
                    <img key={i} src="/placeworthy-mark.png" alt="" aria-hidden="true" className="invert w-6 h-6" />
                  ))}
                </span>
              </div>
              <h3 className="font-serif text-2xl font-semibold mt-4 group-hover:text-claret transition-colors">{w.name}</h3>
              <p className="text-sm text-ink-soft mt-2 leading-relaxed line-clamp-2">{a.note}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-paper-dark relative grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 relative">
          <div className="max-w-2xl">
            <SectionLabel>How the distinction works</SectionLabel>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold mt-4 display-balance">
              One mark, two, or three.
            </h2>
            <p className="text-ink-soft mt-5 text-lg leading-relaxed">
              After every in-person review, the most exceptional places earn the Placeworthy distinction —
              one to three marks. Drill into any place to see how it scored across our five criteria.
            </p>
          </div>
          <div className="mt-12 border-t border-ink/12">
            {[3, 2, 1].map((lvl) => (
              <div key={lvl} className="grid md:grid-cols-[180px_1fr] gap-4 md:gap-10 items-center py-8 border-b border-ink/12">
                <div className="flex items-center gap-1.5">
                  {Array.from({ length: lvl }).map((_, i) => (
                    <img key={i} src="/placeworthy-mark.png" alt="" aria-hidden="true" className="w-10 h-10" />
                  ))}
                </div>
                <div>
                  <h3 className="font-serif text-3xl font-semibold">{DISTINCTIONS[lvl].label}</h3>
                  <p className="text-ink-soft leading-relaxed mt-1">
                    {DISTINCTIONS[lvl].sub}
                    {lvl === 3 && " — a place worth building a trip around."}
                    {lvl === 2 && " — outstanding cooking and hospitality in its class."}
                    {lvl === 1 && " — a genuinely rewarding visit we happily recommend."}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
